import { useEffect, useCallback, useRef } from 'react';
import type { Map, MapMouseEvent, GeoJSONSource, PointLike } from 'mapbox-gl';
import type { Point } from 'geojson';
import type { Unit } from '@/types';
import { HOVER_CONFIG, ZOOM_THRESHOLDS, BOUNDS_PADDING, calculateWeightedCentroidBounds, isValidLocation } from '../mapConfig';
import type { UseMobileDrawerReturn } from '@/hooks/useMobileDrawer';
import mapboxgl from 'mapbox-gl';

/**
 * Map Event Handlers Hook
 *
 * Coordinates all map interaction events:
 * - Cluster clicks (zoom to expand at zoom 9+)
 * - Marker hover (show UnitHoverCard, set cursor)
 * - Marker click (navigate to unit detail)
 * - Mobile click behavior (first click hover, second click navigate)
 * - Background click (deselect unit)
 *
 * Performance: useCallback to prevent handler recreation, debounced hover leave
 */

interface UseMapEventsProps {
  map: Map | null;
  mapLoaded: boolean;
  styleLoaded: number; // Increment when style changes to re-register event handlers
  sourceId: string;
  isMobile: boolean;
  units: Unit[]; // All filtered units for zoom-out snap
  onUnitClick?: (unit: Unit | null) => void;
  onUnitHover?: (unit: Unit | null) => void;
  onClusterHover?: (data: { units: Unit[]; coordinates: [number, number] } | null) => void;
  onClusterClick?: (data: { units: Unit[]; coordinates: [number, number] }) => void;
  hoverLeaveTimeoutRef?: React.MutableRefObject<NodeJS.Timeout | null>; // Shared timeout ref for coordination
  mobileDrawer?: UseMobileDrawerReturn; // Mobile drawer for info display
  filterVersion?: number; // Increment to signal filter change and clear hover state
}

export function useMapEvents({
  map,
  mapLoaded,
  styleLoaded,
  sourceId,
  isMobile,
  units,
  onUnitClick,
  onUnitHover,
  onClusterHover,
  onClusterClick: _onClusterClick,
  hoverLeaveTimeoutRef: externalHoverLeaveTimeoutRef,
  mobileDrawer,
  filterVersion,
}: UseMapEventsProps) {
  // Use external ref if provided, otherwise create internal ref
  const internalHoverLeaveTimeout = useRef<NodeJS.Timeout | null>(null);
  const hoverLeaveTimeout = externalHoverLeaveTimeoutRef || internalHoverLeaveTimeout;
  const hoveredMarkerId = useRef<string | null>(null);
  const skipNextLeave = useRef<boolean>(false); // Set when entering a new marker before leave fires
  const mobileClickedUnitId = useRef<string | null>(null); // Track first mobile click
  const hoverActivationTimeout = useRef<NodeJS.Timeout | null>(null); // Track hover activation delay
  const isClusterHoverActive = useRef<boolean>(false); // Track if cluster popup is currently active
  const isDragging = useRef<boolean>(false); // Track if map is being dragged to prevent false hover clears

  // === CUSTOM ZOOM BEHAVIOR REFS ===
  const singleClickTimeoutRef = useRef<number | null>(null);

  // For zoom-out snap to all units
  const referenceZoomRef = useRef<number | null>(null);
  const lastZoomRef = useRef<number | null>(null);
  const zoomSnapTimeoutRef = useRef<number | null>(null);

  // Ref to track units for zoom operations (avoids stale closures)
  const unitsRef = useRef<Unit[]>(units);

  // Keep unitsRef in sync
  useEffect(() => {
    unitsRef.current = units;
  }, [units]);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverLeaveTimeout.current) {
        clearTimeout(hoverLeaveTimeout.current);
      }
      if (hoverActivationTimeout.current) {
        clearTimeout(hoverActivationTimeout.current);
      }
      if (singleClickTimeoutRef.current) {
        clearTimeout(singleClickTimeoutRef.current);
      }
      if (zoomSnapTimeoutRef.current) {
        clearTimeout(zoomSnapTimeoutRef.current);
      }
    };
  }, []);

  // Clear hover state when filters change (prevents stale closures after filter/zoom)
  // Note: We use a separate effect that returns a cleanup function to avoid
  // mutating refs directly in the effect body (which violates react-hooks/immutability)
  useEffect(() => {
    // Skip initial render (filterVersion is undefined or 0)
    if (!filterVersion) return;

    // Clear all pending timeouts (don't set refs to null - let timeout handlers do that)
    if (hoverLeaveTimeout.current) {
      clearTimeout(hoverLeaveTimeout.current);
    }
    if (hoverActivationTimeout.current) {
      clearTimeout(hoverActivationTimeout.current);
    }

    // Reset tracking refs (these are internal refs, safe to modify)
    hoveredMarkerId.current = null;
    mobileClickedUnitId.current = null;
    isClusterHoverActive.current = false;
    isDragging.current = false; // CRITICAL: Reset drag state to prevent hover blocking

    // Reset cursor if map exists
    if (map) {
      map.getCanvas().style.cursor = '';
    }
  }, [filterVersion, map, hoverLeaveTimeout]);

  // Track drag state to prevent false hover clears during pan/drag
  // When the map is dragged, Mapbox fires mouseleave events on layers as the map moves under the cursor
  useEffect(() => {
    if (!map || !mapLoaded) return;

    const handleDragStart = () => {
      isDragging.current = true;
    };

    const handleDragEnd = () => {
      isDragging.current = false;
    };

    // Safety net: Reset isDragging after any map interaction completes
    // This handles wheel zoom and other interactions that don't fire drag events
    const handleInteractionEnd = () => {
      isDragging.current = false;
    };

    map.on('dragstart', handleDragStart);
    map.on('dragend', handleDragEnd);
    map.on('moveend', handleInteractionEnd);
    map.on('zoomend', handleInteractionEnd);

    return () => {
      map.off('dragstart', handleDragStart);
      map.off('dragend', handleDragEnd);
      map.off('moveend', handleInteractionEnd);
      map.off('zoomend', handleInteractionEnd);
      isDragging.current = false; // CRITICAL: Reset on cleanup to prevent stuck drag state
    };
  }, [map, mapLoaded]);

  // Parse unit data from GeoJSON feature
  const parseUnitData = useCallback((feature: GeoJSON.Feature): Unit | null => {
    try {
      const unitData = feature.properties?.unitData;
      return unitData ? JSON.parse(unitData) : null;
    } catch {
      return null;
    }
  }, []);

  // Wrapper for onClusterHover to track state
  const handleClusterHoverWrapper = useCallback((data: { units: Unit[]; coordinates: [number, number] } | null) => {
    isClusterHoverActive.current = data !== null;
    if (data !== null) {
      // CRITICAL: Clear individual unit hover when activating cluster hover.
      // Without this, rapid cluster enter/leave cycling (from sub-element boundaries)
      // briefly clears hoveredCluster. The stale hoveredUnit then causes a unit popup
      // to flash over the cluster before the cluster popup re-establishes.
      hoveredMarkerId.current = null;
      onUnitHover?.(null);
    }
    onClusterHover?.(data);
  }, [onClusterHover, onUnitHover]);

  // === GLOBAL MAP CLICK DEBUG ===
  useEffect(() => {
    if (!map || !mapLoaded) return;

    const handleGlobalClick = (e: MapMouseEvent) => {
      // Safety check: ensure style is fully loaded before querying features
      // Without this, Mapbox GL v3 internally accesses style.getModel() which throws
      // TypeError when the style sub-objects haven't finished initializing
      if (!map.isStyleLoaded() || !map.getStyle()) return;

      const features = map.queryRenderedFeatures(e.point);
      const clusterFeatures = features.filter(f =>
        f.layer?.id === 'clusters' ||
        false // cluster-count merged into clusters layer
      );

      // If we found a cluster, zoom to fit all its units (like "View All")
      if (clusterFeatures.length > 0) {
        const feature = clusterFeatures[0];
        const clusterId = feature.properties?.cluster_id;
        const source = map.getSource(sourceId) as GeoJSONSource;

        if (!source || !clusterId) return;

        // Clear any existing hover/popup state before zooming
        handleClusterHoverWrapper(null);
        if (isMobile && mobileDrawer) {
          mobileDrawer.closeDrawer?.();
        }

        source.getClusterLeaves(clusterId, Infinity, 0, (err, leaves) => {
          if (err || !leaves || !leaves.length) return;

          const bounds = new mapboxgl.LngLatBounds();
          leaves.forEach((leaf) => {
            const coords = (leaf.geometry as GeoJSON.Point).coordinates;
            bounds.extend(coords as [number, number]);
          });

          map.fitBounds(bounds, {
            padding: isMobile ? BOUNDS_PADDING.mobile : BOUNDS_PADDING.desktop,
            maxZoom: 15,
            duration: 800,
            essential: true,
          });
        });
      } else {
        // CRITICAL FIX: Reset hover blocking ref when clicking non-cluster areas
        // Without this, isClusterHoverActive stays true after clicking anywhere,
        // blocking all marker hover (line 410 check: isClusterHoverActive.current)
        isClusterHoverActive.current = false;
      }
    };

    map.on('click', handleGlobalClick);
    return () => {
      map.off('click', handleGlobalClick);
    };
  }, [map, mapLoaded, styleLoaded, isMobile, mobileDrawer, handleClusterHoverWrapper, parseUnitData, sourceId]);

  // === CLUSTER CLICK (Zoom 9+) ===
  useEffect(() => {
    if (!map || !mapLoaded) return;

    // Check if layer exists before attaching handler
    // CRITICAL: Check isStyleLoaded() BEFORE getStyle() to prevent "Style is not done loading" errors
    if (!map.isStyleLoaded() || !map.getStyle() || !map.getLayer('clusters')) return;

    const handleClusterClick = (e: MapMouseEvent) => {
      if (!map) return;

      // Safety check: ensure style and layer still exist before querying
      if (!map.isStyleLoaded() || !map.getStyle() || !map.getLayer('clusters')) return;

      // Query BOTH layers to catch clicks on either circle or label
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      });
      if (!features.length) return;

      const clusterId = features[0].properties?.cluster_id;
      const source = map.getSource(sourceId) as GeoJSONSource;

      // Clear any existing hover/popup state before zooming (like "View All")
      handleClusterHoverWrapper(null);

      source.getClusterLeaves(clusterId, Infinity, 0, (err, leaves) => {
        if (err || !leaves || !leaves.length) return;

        const bounds = new mapboxgl.LngLatBounds();
        leaves.forEach((leaf) => {
          const coords = (leaf.geometry as GeoJSON.Point).coordinates;
          bounds.extend(coords as [number, number]);
        });

        map.fitBounds(bounds, {
          padding: isMobile ? BOUNDS_PADDING.mobile : BOUNDS_PADDING.desktop,
          maxZoom: 15,
          duration: 800,
          essential: true,
        });
      });
    };

    // Listen to both cluster circle AND label layers
    map.on('click', 'clusters', handleClusterClick);

    return () => {
      // Safe cleanup - check style readiness before attempting to remove handlers
      if (map.isStyleLoaded() && map.getStyle() && map.getLayer('clusters')) {
        map.off('click', 'clusters', handleClusterClick);
      }
    };
  }, [map, mapLoaded, styleLoaded, sourceId, isMobile, handleClusterHoverWrapper, parseUnitData]);

  // === MARKER CLICK (Desktop + Mobile) ===
  // Clicking a marker zooms to it until close enough, then selects it.
  useEffect(() => {
    if (!map || !mapLoaded) return;

    const handleMarkerClick = (e: mapboxgl.MapMouseEvent) => {
      if (!map) return;
      // Use a generous bbox for click detection — small dots (4px) are hard to click precisely
      const clickBbox: [PointLike, PointLike] = [
        [e.point.x - 10, e.point.y - 10],
        [e.point.x + 10, e.point.y + 10],
      ];
      const features = map.queryRenderedFeatures(clickBbox, {
        layers: ['unclustered-markers', 'unclustered-markers-md', 'markers-hitarea', 'unclustered-dots']
          .filter(l => map.getLayer(l)),
      });
      if (!features.length) return;

      const unit = parseUnitData(features[0]);
      if (!unit) return;

      const currentZoom = map.getZoom();

      // Zoom-to-area: below large markers (zoom < 17), clicking a marker zooms
      // to show all nearby units in the area instead of selecting
      if (currentZoom < ZOOM_THRESHOLDS.FULL_MARKERS_MIN) {
        if (singleClickTimeoutRef.current) {
          clearTimeout(singleClickTimeoutRef.current);
          singleClickTimeoutRef.current = null;
        }

        handleClusterHoverWrapper(null);
        onUnitHover?.(null);
        if (isMobile && mobileDrawer) {
          mobileDrawer.closeDrawer?.();
        }

        // Query all visible markers in a generous area around the click
        const NEARBY_RADIUS = 75; // pixels
        const bbox: [PointLike, PointLike] = [
          [e.point.x - NEARBY_RADIUS, e.point.y - NEARBY_RADIUS],
          [e.point.x + NEARBY_RADIUS, e.point.y + NEARBY_RADIUS],
        ];
        const markerLayers = ['unclustered-markers', 'unclustered-markers-md', 'markers-hitarea', 'unclustered-dots']
          .filter(l => map.getLayer(l));
        const nearbyFeatures = map.queryRenderedFeatures(bbox, { layers: markerLayers });

        if (nearbyFeatures.length > 0) {
          // Deduplicate features (same unit can appear on multiple layers)
          const seen = new Set<string>();
          const bounds = new mapboxgl.LngLatBounds();
          nearbyFeatures.forEach((f) => {
            const id = f.properties?.id;
            if (id && seen.has(id)) return;
            if (id) seen.add(id);
            const coords = (f.geometry as GeoJSON.Point).coordinates;
            bounds.extend(coords as [number, number]);
          });

          map.fitBounds(bounds, {
            padding: isMobile ? BOUNDS_PADDING.mobile : BOUNDS_PADDING.desktop,
            maxZoom: ZOOM_THRESHOLDS.FULL_MARKERS_MIN,
            duration: 800,
            essential: true,
          });
        }
        return;
      }

      // At zoom 17+ (large markers): select the unit
      handleClusterHoverWrapper(null);

      if (isMobile) {
        if (mobileDrawer) {
          mobileDrawer.openDrawer({
            type: 'unit_tooltip',
            unit,
          });
        }
        mobileClickedUnitId.current = unit.id;
      } else {
        onUnitClick?.(unit);
        onUnitHover?.(unit);
      }
    };

    // Use map-level click with bbox query instead of layer-specific clicks.
    // Small dots (4px) are nearly impossible to click precisely with layer events.
    const handleMapMarkerClick = (e: mapboxgl.MapMouseEvent) => {
      if (!map || !map.isStyleLoaded() || !map.getStyle()) return;

      // Check if click hit a cluster first (handled by separate handler)
      const clusterFeatures = map.queryRenderedFeatures(e.point, {
        layers: ['clusters'].filter(l => map.getLayer(l)),
      });
      if (clusterFeatures.length > 0) return; // Let cluster handler deal with it

      // Query markers with generous bbox for small dots
      const clickBbox: [PointLike, PointLike] = [
        [e.point.x - 12, e.point.y - 12],
        [e.point.x + 12, e.point.y + 12],
      ];
      const layers = ['unclustered-markers', 'unclustered-markers-md', 'markers-hitarea', 'unclustered-dots']
        .filter(l => map.getLayer(l));
      const features = map.queryRenderedFeatures(clickBbox, { layers });
      if (!features.length) return;

      handleMarkerClick(e);
    };

    map.on('click', handleMapMarkerClick);

    return () => {
      map.off('click', handleMapMarkerClick);
    };
  }, [map, mapLoaded, styleLoaded, isMobile, onUnitClick, onUnitHover, handleClusterHoverWrapper, parseUnitData, mobileDrawer]);

  // === BACKGROUND CLICK (Deselect and close popups) ===
  useEffect(() => {
    if (!map || !mapLoaded) return;

    const handleBackgroundClick = (e: mapboxgl.MapMouseEvent) => {
      if (!map) return;

      // Check if click is inside a popup element (important for mobile)
      const originalEvent = e.originalEvent;
      if (originalEvent && originalEvent.target) {
        const target = originalEvent.target as HTMLElement;
        // Check if the clicked element is inside a Mapbox popup
        if (target.closest('.mapboxgl-popup')) {
          // Click is inside a popup, don't close it
          return;
        }
      }

      // Safety check: ensure style is loaded before querying layers
      if (!map.isStyleLoaded() || !map.getStyle()) return;

      // Only query layers that exist
      const layersToQuery = [
        'unclustered-markers',
        'unclustered-dots',
        'clusters',
      ].filter(layer => map.getLayer(layer));

      const features = map.queryRenderedFeatures(e.point, { layers: layersToQuery });

      // Close hover popup and deselect when clicking on empty map space
      // Note: Clicking ON a marker/cluster is handled by their specific handlers above
      if (!features.length) {
        onUnitClick?.(null);
        onUnitHover?.(null);
        handleClusterHoverWrapper(null); // Also close cluster popups
        mobileClickedUnitId.current = null; // Reset mobile click state

        // Reset cursor since we clicked on empty space
        map.getCanvas().style.cursor = '';

        // Mobile: Close drawer on background click
        if (isMobile && mobileDrawer) {
          mobileDrawer.closeDrawer();
        }
      }
    };

    map.on('click', handleBackgroundClick);
    return () => {
      map.off('click', handleBackgroundClick);
    };
  }, [map, mapLoaded, styleLoaded, onUnitClick, onUnitHover, handleClusterHoverWrapper, isMobile, mobileDrawer]);

  // === MARKER HOVER (Desktop only) ===
  useEffect(() => {
    if (!map || !mapLoaded) return;

    const handleMarkerHover = (e: mapboxgl.MapMouseEvent) => {
      if (!map || isMobile) return;

      // At zoom 1-6, no hover at all (dots too small for meaningful interaction)
      if (map.getZoom() <= ZOOM_THRESHOLDS.NO_HOVER_THRESHOLD) {
        map.getCanvas().style.cursor = 'pointer';
        return;
      }

      // PRIORITY CHECK: If cursor is directly over a cluster feature, do not trigger marker hover.
      // Use a small bbox (5px) to catch cluster features right at the cursor position.
      // Previously used 25-45px radius + isClusterHoverActive flag, which was far too aggressive
      // and caused the flag to get stuck, blocking ALL marker hovers.
      // The popup rendering system (useMapPopups) already gives cluster popups priority,
      // so we only need to prevent the initial hover activation when directly on a cluster.
      const _zoom = map.getZoom();

      // Guard against timing issues during map initialization and style transitions
      if (!map.isStyleLoaded() || !map.getStyle()) return;

      const clusterLayers = ['clusters']
        .filter(layer => map.getLayer(layer));

      // Use a moderate bbox (15px) to catch cluster features near the cursor.
      // Too small (5px) causes glitching at cluster edges; too large (45px) blocks distant markers.
      const clusterBbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
        [e.point.x - 15, e.point.y - 15],
        [e.point.x + 15, e.point.y + 15]
      ];

      const clusterFeatures = clusterLayers.length > 0
        ? map.queryRenderedFeatures(clusterBbox, { layers: clusterLayers })
        : [];

      if (clusterFeatures.length > 0) {
        // CRITICAL: Clear any stale activation timeout from a previous marker.
        // Without this, moving from a marker to a cluster lets the old timeout fire,
        // briefly showing a unit popup over the cluster (visible glitch).
        if (hoverActivationTimeout.current) {
          clearTimeout(hoverActivationTimeout.current);
          hoverActivationTimeout.current = null;
        }
        // Don't show individual unit tooltip when cursor is directly on a cluster
        return;
      }

      // Clear pending hover leave timeout.
      // Also flag that a new marker enter happened — the subsequent leave event
      // for the OLD marker should be ignored (Enter B → Leave A ordering).
      if (hoverLeaveTimeout.current) {
        clearTimeout(hoverLeaveTimeout.current);
        hoverLeaveTimeout.current = null;
      }
      if (hoveredMarkerId.current !== null) {
        // A different marker is currently hovered — entering a new one means
        // the upcoming leave (from the old marker's layer) should be skipped.
        skipNextLeave.current = true;
      }

      map.getCanvas().style.cursor = 'pointer';

      // Use a small bbox (5px) for the feature query to match Mapbox's layer hit-test tolerance.
      // queryRenderedFeatures(point) at a single pixel can miss features that triggered
      // the layer's mouseenter event, causing "first marker does nothing" behavior.
      const featureBbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
        [e.point.x - 5, e.point.y - 5],
        [e.point.x + 5, e.point.y + 5]
      ];
      const features = map.queryRenderedFeatures(featureBbox, {
        layers: ['unclustered-markers', 'unclustered-markers-md', 'markers-hitarea', 'unclustered-dots'],
      });
      if (!features.length) return;

      const hoveredUnit = parseUnitData(features[0]);
      if (hoveredUnit) {
        hoveredMarkerId.current = hoveredUnit.id;

        // Preload unit image immediately on mouseenter (before activation delay).
        // This ensures the image is cached by the time the popup renders,
        // preventing the visible flash/skeleton while the image loads.
        if (hoveredUnit.image) {
          const img = new Image();
          img.src = hoveredUnit.image;
        }

        // Clear any existing hover activation timeout
        if (hoverActivationTimeout.current) {
          clearTimeout(hoverActivationTimeout.current);
        }

        // Minimal delay (10ms) to debounce rapid multi-layer mouseenter events.
        // The cluster proximity check (5px bbox) already filters out cluster overlaps,
        // so we don't need the previous 100ms grace period.
        hoverActivationTimeout.current = setTimeout(() => {
          // Double-check we're still hovering this unit
          if (hoveredMarkerId.current === hoveredUnit.id) {
            onUnitHover?.(hoveredUnit);
          }
        }, 10);  // 10ms debounce for multi-layer mouseenter events
      }
    };

    const handleMarkerLeave = () => {
      if (!map || isMobile) return;
      map.getCanvas().style.cursor = '';

      // CRITICAL: Skip hover clear if map is being dragged
      // When dragging, Mapbox fires mouseleave as the map moves under the cursor
      // This prevents false-positive hover clears that break both map AND sidebar hover
      if (isDragging.current) return;

      // NOTE: We intentionally do NOT clear hoverActivationTimeout here.
      // When moving between adjacent markers, Mapbox fires mouseenter for the NEW marker
      // BEFORE mouseleave for the OLD marker. Clearing the timeout here would cancel
      // the new marker's activation, causing "stuck" behavior where no tooltip appears.
      // The activation timeout (10ms) checks hoveredMarkerId.current which is already
      // updated by handleMarkerHover, ensuring only the correct marker activates.

      // CRITICAL FIX: Clear any existing leave timeout before creating a new one.
      // Multiple marker layers (unclustered-markers, unclustered-dots)
      // fire mouseleave simultaneously. Without this, the first timeout becomes orphaned
      // (its ref is overwritten by the second), so clearLeaveTimeouts() in the popup
      // mouseenter handler can only cancel the last one — the orphaned timeout still fires
      // and kills the hover state.
      if (hoverLeaveTimeout.current) {
        clearTimeout(hoverLeaveTimeout.current);
      }

      // If a new marker enter happened just before this leave (Enter B → Leave A),
      // skip this leave entirely — it's from the old marker's layer.
      if (skipNextLeave.current) {
        skipNextLeave.current = false;
        return;
      }

      // Debounce hover leave to prevent flicker
      hoverLeaveTimeout.current = setTimeout(() => {
        hoveredMarkerId.current = null;
        // NOTE: Do NOT reset isClusterHoverActive here. That flag is managed solely
        // by handleClusterHoverWrapper. Resetting it in the marker leave timeout
        // corrupts cluster state when marker leaves fire during active cluster hover.
        onUnitHover?.(null);
        hoverLeaveTimeout.current = null;
      }, HOVER_CONFIG.leaveDelay);
    };

    map.on('mouseenter', 'unclustered-markers', handleMarkerHover);
    map.on('mouseenter', 'unclustered-markers-md', handleMarkerHover);
    map.on('mouseenter', 'markers-hitarea', handleMarkerHover);
    map.on('mouseenter', 'unclustered-dots', handleMarkerHover);
    map.on('mouseleave', 'unclustered-markers', handleMarkerLeave);
    map.on('mouseleave', 'unclustered-markers-md', handleMarkerLeave);
    map.on('mouseleave', 'markers-hitarea', handleMarkerLeave);
    map.on('mouseleave', 'unclustered-dots', handleMarkerLeave);

    // Fallback: Layer mouseenter does not fire when the cursor is already over the map on page load.
    // Use throttled mousemove to trigger hover when cursor is over a marker but mouseenter never fired.
    let lastMousemoveFallback = 0;
    const MOUSEMOVE_FALLBACK_THROTTLE_MS = 100;
    const handleMapMousemove = (e: mapboxgl.MapMouseEvent) => {
      if (isMobile || !map.isStyleLoaded() || !map.getStyle()) return;
      const now = Date.now();
      if (now - lastMousemoveFallback < MOUSEMOVE_FALLBACK_THROTTLE_MS) return;
      const layers = ['unclustered-markers', 'unclustered-markers-md', 'markers-hitarea', 'unclustered-dots'].filter((l) => map.getLayer(l));
      if (!layers.length) return;
      const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
        [e.point.x - 10, e.point.y - 10],
        [e.point.x + 10, e.point.y + 10],
      ];
      const features = map.queryRenderedFeatures(bbox, { layers });
      if (!features.length) return;
      const unit = parseUnitData(features[0]);
      if (!unit || hoveredMarkerId.current === unit.id) return;
      lastMousemoveFallback = now;
      handleMarkerHover(e);
    };
    map.on('mousemove', handleMapMousemove);

    return () => {
      map.off('mousemove', handleMapMousemove);
      map.off('mouseenter', 'unclustered-markers', handleMarkerHover);
      map.off('mouseenter', 'unclustered-markers-md', handleMarkerHover);
      map.off('mouseenter', 'markers-hitarea', handleMarkerHover);
      map.off('mouseenter', 'unclustered-dots', handleMarkerHover);
      map.off('mouseleave', 'unclustered-markers', handleMarkerLeave);
      map.off('mouseleave', 'unclustered-markers-md', handleMarkerLeave);
      map.off('mouseleave', 'markers-hitarea', handleMarkerLeave);
      map.off('mouseleave', 'unclustered-dots', handleMarkerLeave);
      // CRITICAL: Reset hover blocking ref to prevent stuck state after effect re-run
      isClusterHoverActive.current = false;
    };
  }, [map, mapLoaded, styleLoaded, isMobile, onUnitHover, parseUnitData]);

  // === CLUSTER HOVER (Desktop only, zoom 9+ for clusters) ===
  useEffect(() => {
    if (!map || !mapLoaded || isMobile) return;

    const handleClusterHover = (clusterLayer: string, source: string) => (e: mapboxgl.MapMouseEvent) => {
      if (!map) return;
      
      // Safety check: ensure style is loaded and layer exists before querying
      if (!map.isStyleLoaded() || !map.getStyle() || !map.getLayer(clusterLayer)) return;
      
      map.getCanvas().style.cursor = 'pointer';

      // Clear any pending marker leave timeout - marker leaves should not interfere with cluster hover
      if (hoverLeaveTimeout.current) {
        clearTimeout(hoverLeaveTimeout.current);
        hoverLeaveTimeout.current = null;
      }

      const features = map.queryRenderedFeatures(e.point, { layers: [clusterLayer] });
      if (!features.length) return;

      const clusterId = features[0].properties?.cluster_id;
      const coordinates = (features[0].geometry as GeoJSON.Point).coordinates as [number, number];
      const geoSource = map.getSource(source) as mapboxgl.GeoJSONSource;

      geoSource.getClusterLeaves(clusterId, Infinity, 0, (err, leaves) => {
        if (err || !leaves) return;

        const clusterUnits = leaves
          .map(parseUnitData)
          .filter((u): u is Unit => u !== null && u.id !== undefined);

        if (clusterUnits.length > 0) {
          handleClusterHoverWrapper({ units: clusterUnits, coordinates });
        }
      });
    };

    const handleClusterLeave = () => {
      if (!map) return;
      map.getCanvas().style.cursor = '';

      // Add small delay before clearing cluster hover to allow mouse to reach popup
      // This prevents flicker when moving from cluster to its popup
      if (hoverLeaveTimeout.current) {
        clearTimeout(hoverLeaveTimeout.current);
      }

      hoverLeaveTimeout.current = setTimeout(() => {
        // Check if mouse is now over a popup element before clearing
        // Use document.elementFromPoint with last known mouse position
        const popupElements = document.querySelectorAll('.mapboxgl-popup');
        let isOverPopup = false;

        // Check if any popup is currently being hovered
        // Note: This is a simplified check - if popups exist and are visible, 
        // we assume user may be moving to one
        popupElements.forEach((popup) => {
          if (popup.matches(':hover')) {
            isOverPopup = true;
          }
        });

        // Only clear if not over a popup
        if (!isOverPopup) {
          handleClusterHoverWrapper(null);
        }
      }, 150); // 150ms delay to cover sub-element boundary gaps
    };

    // Hover for clusters (zoom 9+, 50px radius, 4+ units)
    // CRITICAL: Store handler reference in a variable to ensure the same reference
    // is used for both registration AND cleanup.
    const clusterHoverHandler = handleClusterHover('clusters', sourceId);
    const clusterHitareaHoverHandler = handleClusterHover('clusters', sourceId);
    map.on('mouseenter', 'clusters', clusterHoverHandler);
    map.on('mouseleave', 'clusters', handleClusterLeave);
    if (map.getLayer('clusters-hitarea')) {
      map.on('mouseenter', 'clusters-hitarea', clusterHitareaHoverHandler);
      map.on('mouseleave', 'clusters-hitarea', handleClusterLeave);
    }

    return () => {
      map.off('mouseenter', 'clusters', clusterHoverHandler);
      map.off('mouseleave', 'clusters', handleClusterLeave);
      if (map.getLayer('clusters-hitarea')) {
        map.off('mouseenter', 'clusters-hitarea', clusterHitareaHoverHandler);
        map.off('mouseleave', 'clusters-hitarea', handleClusterLeave);
      }
      // CRITICAL: Reset hover blocking ref to prevent stuck state after effect re-run
      isClusterHoverActive.current = false;
    };
  }, [map, mapLoaded, styleLoaded, isMobile, sourceId, handleClusterHoverWrapper, parseUnitData]);

  // === CUSTOM ZOOM BEHAVIORS ===
  // 1. Double-click: Zoom to level 15 and center on nearest marker
  // 2. Zoom-out snap: Fit all filtered units when 2+ levels below threshold
  // 3. Single-click (empty area): Zoom in 4 levels
  useEffect(() => {
    if (!map || !mapLoaded) return;

    const QUERY_RADIUS = 100; // pixels for queryRenderedFeatures bbox

    // === DOUBLE-CLICK TO NEAREST MARKER ===
    const handleDoubleClick = (e: MapMouseEvent) => {
      if (!map) return;

      // Cancel any pending single-click zoom
      if (singleClickTimeoutRef.current) {
        clearTimeout(singleClickTimeoutRef.current);
        singleClickTimeoutRef.current = null;
      }

      const point = e.point;

      // Query visible features in a radius around click (GPU-accelerated)
      const bbox: [PointLike, PointLike] = [
        [point.x - QUERY_RADIUS, point.y - QUERY_RADIUS],
        [point.x + QUERY_RADIUS, point.y + QUERY_RADIUS]
      ];

      // Safety check: ensure style is loaded
      if (!map.isStyleLoaded() || !map.getStyle()) return;

      // Get marker layers that exist
      const markerLayers = ['unclustered-markers', 'unclustered-markers-md', 'markers-hitarea', 'unclustered-dots'].filter(
        layer => map.getLayer(layer)
      );

      if (markerLayers.length === 0) {
        // No marker layers - fallback to default zoom (zoom in 1 level at click point)
        map.flyTo({
          center: e.lngLat,
          zoom: map.getZoom() + 1,
          duration: 300
        });
        return;
      }

      const features = map.queryRenderedFeatures(bbox, { layers: markerLayers });

      if (features.length === 0) {
        // No nearby units - fall back to default zoom behavior (zoom in 1 level at click point)
        map.flyTo({
          center: e.lngLat,
          zoom: map.getZoom() + 1,
          duration: 300
        });
        return;
      }

      // Find nearest feature to click point (pixel distance)
      let nearest = features[0];
      let minDist = Infinity;

      for (const feature of features) {
        const coords = (feature.geometry as Point).coordinates;
        const featurePoint = map.project([coords[0], coords[1]]);
        const dist = Math.hypot(featurePoint.x - point.x, featurePoint.y - point.y);
        if (dist < minDist) {
          minDist = dist;
          nearest = feature;
        }
      }

      const coords = (nearest.geometry as Point).coordinates;

      // Dynamic zoom: 13 if dense area (5+ units nearby), 15 otherwise
      const targetZoom = features.length >= 5 ? 13 : 15;

      map.flyTo({
        center: [coords[0], coords[1]],
        zoom: targetZoom,
        duration: 1000,
        essential: true
      });
    };

    // Disable default double-click zoom and add custom handler
    map.doubleClickZoom.disable();
    map.on('dblclick', handleDoubleClick);

    // === ZOOM-OUT SNAP TO ALL FILTERED UNITS ===
    // Initialize reference zoom from current map zoom
    if (referenceZoomRef.current === null) {
      referenceZoomRef.current = map.getZoom();
      lastZoomRef.current = map.getZoom();
    }

    const handleZoom = () => {
      if (!map) return;
      lastZoomRef.current = map.getZoom();
    };

    const handleZoomEnd = () => {
      if (!map) return;

      // Clear any pending snap
      if (zoomSnapTimeoutRef.current) {
        clearTimeout(zoomSnapTimeoutRef.current);
        zoomSnapTimeoutRef.current = null;
      }

      const currentZoom = map.getZoom();
      const reference = referenceZoomRef.current;
      const lastZoom = lastZoomRef.current;

      // Determine if we were zooming out
      const wasZoomingOut = lastZoom !== null && currentZoom < lastZoom;

      if (!wasZoomingOut || reference === null) {
        // Update reference if zooming IN (allows exploration without snap)
        if (lastZoom !== null && currentZoom > lastZoom) {
          referenceZoomRef.current = currentZoom;
        }
        return;
      }

      // Check if zoomed out 2+ levels below reference
      const currentUnits = unitsRef.current;
      if (currentZoom <= reference - 2 && currentUnits.length > 0) {
        // Debounce: wait 150ms before snapping
        zoomSnapTimeoutRef.current = window.setTimeout(() => {
          if (!map) return;

          const unitsWithLocation = currentUnits.filter(u => isValidLocation(u.location));
          if (unitsWithLocation.length === 0) return;

          const coordinates = unitsWithLocation.map(u => ({
            lat: u.location.lat,
            lng: u.location.lng
          }));
          const weightedBounds = calculateWeightedCentroidBounds(coordinates, 0.95);

          if (weightedBounds) {
            const bounds = new mapboxgl.LngLatBounds(weightedBounds.sw, weightedBounds.ne);
            map.fitBounds(bounds, {
              padding: 50,
              maxZoom: 6,
              duration: 1000,
              essential: true
            });

            // Update reference to new fitted zoom (after animation completes)
            setTimeout(() => {
              if (map) {
                referenceZoomRef.current = map.getZoom();
              }
            }, 1100);
          }
        }, 150);
      }
    };

    map.on('zoom', handleZoom);
    map.on('zoomend', handleZoomEnd);

    return () => {
      if (map) {
        map.doubleClickZoom.enable();
        map.off('dblclick', handleDoubleClick);
        map.off('zoom', handleZoom);
        map.off('zoomend', handleZoomEnd);
      }

      // Clean up timeouts
      if (singleClickTimeoutRef.current) {
        clearTimeout(singleClickTimeoutRef.current);
        singleClickTimeoutRef.current = null;
      }
      if (zoomSnapTimeoutRef.current) {
        clearTimeout(zoomSnapTimeoutRef.current);
        zoomSnapTimeoutRef.current = null;
      }
    };
  }, [map, mapLoaded, styleLoaded, isMobile]);

  // === UPDATE REFERENCE ZOOM ON FILTER CHANGE ===
  // When filters change, update the reference zoom to current level
  useEffect(() => {
    if (!map || !mapLoaded || !filterVersion) return;

    // Reset reference zoom when filters change
    // This ensures the new filtered set defines the "home" zoom
    const currentZoom = map.getZoom();
    referenceZoomRef.current = currentZoom;
    lastZoomRef.current = currentZoom;
  }, [map, mapLoaded, filterVersion]);
}
